import { useEffect, useRef, useState } from "react";
import { isHomePath, waitForHomeHeroMedia } from "../utils/homeHeroLoader";

const INITIAL_FONT_WAIT_MS = 250;
const INITIAL_FADE_MS = 200;
const ROUTE_FINISH_MS = 180;
const ROUTE_HIDE_MS = 520;

function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((resolve) => setTimeout(resolve, ms)),
  ]);
}

function useAppLoader(routeKey) {
  const [isLoaderVisible, setIsLoaderVisible] = useState(true);
  const [isLoaderFinishing, setIsLoaderFinishing] = useState(false);
  const [isBootstrapped, setIsBootstrapped] = useState(false);
  const previousRouteKeyRef = useRef(routeKey);
  const initialRouteKeyRef = useRef(routeKey);

  useEffect(() => {
    let isCancelled = false;

    const waitForFonts = () =>
      document.fonts?.ready instanceof Promise
        ? document.fonts.ready.catch(() => undefined)
        : Promise.resolve();

    const runLoader = async () => {
      await withTimeout(waitForFonts(), INITIAL_FONT_WAIT_MS);
      if (isCancelled) return;

      // On home, keep the splash until the hero video/image is ready to show.
      if (isHomePath(initialRouteKeyRef.current)) {
        await waitForHomeHeroMedia();
        if (isCancelled) return;
      }

      setIsLoaderFinishing(true);
      setTimeout(() => {
        if (!isCancelled) {
          setIsLoaderVisible(false);
          setIsBootstrapped(true);
        }
      }, INITIAL_FADE_MS);
    };

    runLoader();

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isBootstrapped) return;
    if (previousRouteKeyRef.current === routeKey) return;
    previousRouteKeyRef.current = routeKey;

    let isCancelled = false;
    let finishTimer;
    let hideTimer;

    setIsLoaderVisible(true);
    setIsLoaderFinishing(false);

    const runRouteLoader = async () => {
      if (isHomePath(routeKey)) {
        await waitForHomeHeroMedia();
        if (isCancelled) return;
        setIsLoaderFinishing(true);
        hideTimer = setTimeout(() => {
          if (!isCancelled) setIsLoaderVisible(false);
        }, INITIAL_FADE_MS);
        return;
      }

      finishTimer = setTimeout(() => {
        if (!isCancelled) setIsLoaderFinishing(true);
      }, ROUTE_FINISH_MS);

      hideTimer = setTimeout(() => {
        if (!isCancelled) setIsLoaderVisible(false);
      }, ROUTE_HIDE_MS);
    };

    runRouteLoader();

    return () => {
      isCancelled = true;
      clearTimeout(finishTimer);
      clearTimeout(hideTimer);
    };
  }, [isBootstrapped, routeKey]);

  return { isLoaderVisible, isLoaderFinishing };
}

export default useAppLoader;
